/**
 * As tintas dos grupos de dado — uma fonte só, para ficha, cartão e evento.
 *
 * Isto morava dentro de `components/Sinais.tsx`, que é onde a decisão foi tomada
 * e onde o raciocínio está escrito por extenso. Saiu de lá quando o registro de
 * sinais de evento (`lib/eventos.ts`) precisou das mesmas tintas: um componente
 * importando de outro componente para pegar cor cria a dependência ao contrário,
 * e a alternativa — copiar os hexadecimais — garante que os dois saiam de
 * sincronia na primeira vez que alguém ajustar um tom.
 *
 * O resumo da decisão, para quem chegar por aqui: o sistema reserva `ok` oliva,
 * `warn` ocre e `error` ferrugem para ESTADO. Estas tintas são de ESPÉCIE, e
 * nenhuma delas é confundível com o trio de estado — é isso que protege o
 * vocabulário, não a ausência de cor.
 */

import { corDeLab, paraLab, parseCor, type Lab, type Rgb } from '@vestigo/core/design-cor';

export type TipoDeSinal =
  /** UTM, referenciador, campanha — de onde a pessoa veio. */
  | 'origem'
  /** fbclid, gclid, fbp, external_id, sck — identidade de máquina. */
  | 'identificacao'
  /** Valor, pedido, oferta, checkout, pagamento — o que foi vendido. */
  | 'comercio'
  /** Celular, navegador, sistema — carrega marca em vez de cor. */
  | 'aparelho'
  /** Rolagem, vídeo, parcelas — o coro. Fica vazada. */
  | 'medida';

/**
 * A tinta base de cada grupo, sólida.
 *
 * `origem` reusa o acento porque ele já significa origem nesta tela; ameixa e
 * ciano não são lidos como veredito por ninguém.
 */
export const TINTA_DO_GRUPO: Record<TipoDeSinal, string> = {
  origem: 'var(--color-accent)',
  identificacao: '#6b4f7a',
  comercio: '#1f6b73',
  aparelho: 'var(--color-dark)',
  medida: 'var(--color-dark)',
};

/**
 * O preenchimento de cada grupo, na força da ficha.
 *
 * A lombada de 2px foi uma tentativa de não gastar cor. Na tela real o efeito
 * foi o oposto: um fio de 2px na borda de uma ficha de 22px some no meio de doze
 * fichas, e o que se via era uma fileira de caixas brancas com um cisco
 * colorido. O preenchimento é o que o olho lê primeiro, e era ali que a
 * distinção precisava estar.
 */
export const PREENCHIMENTO: Record<TipoDeSinal, { fundo: string; borda: string; texto: string }> = {
  origem: {
    fundo: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
    borda: 'color-mix(in srgb, var(--color-accent) 30%, transparent)',
    texto: 'var(--color-accent)',
  },
  identificacao: {
    fundo: 'color-mix(in srgb, #6b4f7a 10%, transparent)',
    borda: 'color-mix(in srgb, #6b4f7a 28%, transparent)',
    texto: '#5b4268',
  },
  comercio: {
    fundo: 'color-mix(in srgb, #1f6b73 10%, transparent)',
    borda: 'color-mix(in srgb, #1f6b73 28%, transparent)',
    texto: '#1b5b62',
  },
  aparelho: {
    fundo: 'color-mix(in srgb, var(--color-dark) 6%, transparent)',
    borda: 'color-mix(in srgb, var(--color-dark) 16%, transparent)',
    texto: 'color-mix(in srgb, var(--color-dark) 75%, transparent)',
  },
  medida: {
    fundo: 'transparent',
    borda: 'color-mix(in srgb, var(--color-dark) 16%, transparent)',
    texto: 'color-mix(in srgb, var(--color-dark) 65%, transparent)',
  },
};

/** Nome do grupo, para a legenda e para o `title` de quem usa leitor de tela. */
export const NOME_DO_GRUPO: Record<TipoDeSinal, string> = {
  origem: 'Origem',
  identificacao: 'Identificação',
  comercio: 'Comércio',
  aparelho: 'Aparelho',
  medida: 'Medida',
};

/**
 * O tom de fundo de cada cartão da coluna da esquerda.
 *
 * A coluna empilha cinco ou seis cartões sobre o mesmo fundo, todos com o mesmo
 * fio: rolando a página, eles viram uma faixa contínua e o operador perde de
 * vista qual assunto está lendo. Dar um tom a cada um transforma a rolagem em
 * navegação — reconhece-se "o cartão de origem" pela cor antes de ler o título.
 *
 * A 5%, metade da opacidade da ficha: um cartão inteiro tingido na força da
 * ficha viraria um bloco de cor com texto por cima.
 */
export type TomDeCartao = TipoDeSinal | 'neutro';

export function fundoDoCartao(tom: TomDeCartao): string | undefined {
  if (tom === 'neutro') return undefined;
  return `color-mix(in srgb, ${TINTA_DO_GRUPO[tom]} 5%, var(--color-surface))`;
}

/**
 * ── AS TINTAS DAS PLATAFORMAS DE MÍDIA ──────────────────────────────────────
 *
 * Espécie, e não estado — a mesma regra do bloco acima, com outro sujeito.
 * `ok` oliva, `warn` ocre e `error` ferrugem continuam reservados a VEREDITO, e
 * nenhuma das cinco abaixo é confundível com eles: o par mais próximo é
 * `youtube` × `error` a **ΔE 19,4**, e os outros catorze passam de 24.
 *
 * ## Por que cor entra aqui e não entrou na curva
 *
 * Porque aqui existem CATEGORIAS. Numa série única hue não separa nada e vira
 * decoração; com a série repartida por plataforma, a cor é a única coisa que
 * responde *de quem foi o pico* sem obrigar o operador a passar o cursor por
 * trinta dias. O dado veio junto (`series.byPlatform`), e é ele que torna a
 * cor informação.
 *
 * ## Quatro das cinco já existiam nesta casa
 *
 * `accent`, o ciano de `comercio` e a ameixa de `identificacao` (nos tons de
 * TEXTO, que são os que passam o piso) e o próprio `dark`. **Um único hex é
 * novo — o vinho** —, e ele entrou porque as alternativas medidas colidiam:
 * bronze ficava a ΔE 10,4 de `warn`, oliva a 14,6 de `ok`, roxo a 13,6 da
 * ameixa e grafite a 13,0 do accent.
 *
 * ## E elas são mnemônicas de propósito
 *
 * Meta é azul, YouTube é vermelho, TikTok é preto. Quem opera as três já tem
 * essa associação pronta do gerenciador nativo, e uma tinta que briga com ela
 * custa uma tradução a cada leitura.
 *
 * Medido com `packages/core/src/design/cor.ts`, como TEXTO sobre as quatro
 * superfícies do produto (papel · mesa · prancheta · claro):
 *
 * | plataforma | tinta | papel | mesa | prancheta | claro |
 * |---|---|---:|---:|---:|---:|
 * | `meta` | `#3f556b` accent | 6,55 | 6,07 | **5,16** | 7,10 |
 * | `google_ads` | `#1b5b62` ciano | 6,56 | 6,08 | **5,17** | 7,11 |
 * | `openai_ads` | `#5b4268` ameixa | 7,36 | 6,82 | **5,80** | 7,97 |
 * | `youtube` | `#7a3b52` vinho · **novo** | 6,93 | 6,42 | **5,46** | 7,50 |
 * | `tiktok` | `#2d322f` dark | 11,08 | 10,27 | **8,74** | 12,01 |
 *
 * Nenhuma desce de 5,16 — o piso é 4,5.
 *
 * **Hex e não `var(--color-*)`**: o ECharts desenha em canvas e não lê variável
 * de CSS. É a mesma razão de `CHART` existir, e o mesmo cuidado se aplica —
 * nenhuma cerca de Tailwind alcança um objeto TypeScript, então quem mexer
 * aqui responde a `o-eixo-do-grafico-se-le.test.ts`.
 */
export const TINTA_DA_PLATAFORMA: Record<string, string> = {
  meta: '#3f556b',
  google_ads: '#1b5b62',
  openai_ads: '#5b4268',
  youtube: '#7a3b52',
  tiktok: '#2d322f',
};

/**
 * ── AS MARCAS DAS REDES, NA COR DELAS — E POR QUE ELAS NÃO SÃO AS DE CIMA ───
 *
 * ## O defeito que estas tintas apagam, e ele custou uma série
 *
 * Em 03/09/2026 o dono apagou o vigiado de Instagram **julgando-o duplicata**: a
 * lista mostrava a mesma marca (`promovaweb`) em duas redes e **não dizia a rede
 * em texto**, e o ícone das duas era o mesmo traço monocromático de 14 px. Foram
 * com o gesto quatro dias de série, as publicações, as passadas e as 6 capturas
 * cruas. **A tela causou o gesto.**
 *
 * ## Por que não dá para reusar `TINTA_DA_PLATAFORMA`
 *
 * Porque as duas respondem perguntas diferentes, e o teste que as separa é o
 * fundo: as de cima são **tinta de série**, escolhidas para serem lidas como
 * TEXTO sobre as quatro superfícies do produto (nenhuma abaixo de 5,16:1) e
 * separadas entre si por ΔE ≥ 16 — o vinho `#7a3b52` do YouTube existe porque o
 * vermelho de marca colidia com `error`. Estas são **tinta de logotipo**: só
 * aparecem dentro do glifo, com o desenho branco vazado por cima, e nunca como
 * cor de texto. Trocar uma pela outra faria um logotipo em vinho (que não é o
 * YouTube de ninguém) ou um `error` a 4 pontos de ΔE do vermelho de marca.
 *
 * ## O contraste, medido — e o Instagram passa por causa do gradiente
 *
 * `packages/core/src/design/cor.ts`, contra as superfícies onde a marca aparece:
 *
 * | tinta | papel `#ebedea` | mesa `#e2e5e1` | claro `#f4f6f3` | escuro `#1e2420` |
 * |---|---:|---:|---:|---:|
 * | `youtube` `#FF0000` | 3,40 | 3,15 | 3,68 | **3,95** |
 * | `instagram` roxo `#8134AF` | **5,86** | 5,43 | 6,35 | 2,29 |
 * | `instagram` laranja `#F58529` | 2,16 | 2,00 | 2,34 | **6,23** |
 * | `tiktok` preto `#2d322f` | 11,08 | 10,27 | 12,01 | 1,21 |
 * | `tiktok` ciano `#25F4EE` | 1,17 | 1,08 | 1,27 | **11,48** |
 * | `tiktok` magenta `#FE2C55` | **3,13** | 2,90 | **3,39** | **4,30** |
 *
 * O piso de um elemento gráfico não textual é **3:1**, e é ele que se aplica —
 * estas cores nunca carregam letra. O YouTube passa nas quatro. **O Instagram e
 * o TikTok passam por serem de duas pontas**: a ponta escura carrega no claro, a
 * clara carrega no escuro, e é exactamente por isso que os dois logotipos são
 * feitos assim. Uma marca de tinta única no meio da faixa é a que desaparece de
 * um dos lados — e é o que aconteceria se alguém «simplificasse» o gradiente
 * para o rosa do meio (3,80 / 3,53).
 *
 * ## E o nome escrito viaja SEMPRE ao lado
 *
 * Cor acrescenta, não substitui: estas telas viajam em print no chat da equipe,
 * e um print em cinzento perde a distinção entre o gradiente e o vermelho. Quem
 * separa as duas redes é a palavra; a cor faz a leitura ser de relance.
 */
export const MARCA_DA_REDE = {
  youtube: '#FF0000',
  /* As quatro paradas do gradiente, da laranja ao azul — a ordem é a do
     logotipo, do canto de baixo à esquerda ao de cima à direita. */
  instagramLaranja: '#F58529',
  instagramRosa: '#DD2A7B',
  instagramRoxo: '#8134AF',
  instagramAzul: '#515BD4',
  /* O preto do TikTok é o `dark` desta casa e não `#000`: um preto puro é a
     única tinta do produto que não existe em lugar nenhum, e a diferença é
     invisível ao lado do ciano e do magenta que o acompanham. */
  tiktok: '#2d322f',
  tiktokCiano: '#25F4EE',
  tiktokMagenta: '#FE2C55',
  /** O vazado do glifo — o triângulo do YouTube e a câmara do Instagram. */
  vazado: '#ffffff',
} as const;

/**
 * A tinta de uma plataforma que o registro não conhece.
 *
 * Um provedor novo entra em `campaign_spend_daily.platform` como texto livre —
 * a coluna é `text` de propósito, e o comentário dela diz por quê. Devolver
 * `undefined` faria o ECharts escolher da paleta padrão dele, que é saturada e
 * não é desta casa; devolver o cinza do eixo faz a faixa aparecer **sem
 * afirmar identidade nenhuma**, que é a verdade sobre ela.
 */
export const TINTA_DE_PLATAFORMA_DESCONHECIDA = 'rgba(45, 50, 47, 0.55)';

/**
 * A textura de cada plataforma — a segunda dimensão, para o print em cinza.
 *
 * Cor **acrescenta**, não substitui: cinco faixas empilhadas distinguidas só
 * por hue somem juntas num print em preto e branco, e é assim que este número
 * viaja no chat da equipe. Cada faixa carrega um `decal` do ECharts com ângulo
 * e densidade próprios, a 12% — visível de perto, invisível de longe, e é ele
 * que sobrevive à conversão para cinza.
 *
 * `dashArrayX`/`dashArrayY` diferentes **e** `rotation` diferente: só o ângulo
 * não basta, porque 45° e 135° viram a mesma textura numa faixa de 20px de
 * altura.
 */
export const TEXTURA_DA_PLATAFORMA: Record<
  string,
  { dashArrayX: number[]; dashArrayY: number[]; rotation: number }
> = {
  meta: { dashArrayX: [1, 0], dashArrayY: [2, 5], rotation: Math.PI / 4 },
  google_ads: { dashArrayX: [1, 0], dashArrayY: [4, 3], rotation: -Math.PI / 4 },
  openai_ads: { dashArrayX: [3, 3], dashArrayY: [3, 3], rotation: 0 },
  youtube: { dashArrayX: [1, 0], dashArrayY: [6, 4], rotation: Math.PI / 2 },
  tiktok: { dashArrayX: [2, 4], dashArrayY: [1, 0], rotation: 0 },
};

/**
 * ── A RAMPA DE DENSIDADE ────────────────────────────────────────────────────
 *
 * Cinco paradas quentes, e a densidade escolhe uma posição entre elas. O
 * raciocínio inteiro — a medição, o que reprovou e por que estes cinco
 * hexadecimais e não vizinhos — está no `@theme` de `apps/web/src/index.css`,
 * junto da emenda que registra a exceção do dono.
 *
 * ## Por que ela mora AQUI, e não no `@theme` como morava
 *
 * Até 29/08/2026 esta rampa era **uma tinta** (`--color-densidade`, `#904318`) e
 * seis degraus de alfa, e a tinta existia em dois lugares: o token do tema e uma
 * cópia em TypeScript, porque um `<canvas>` não lê variável de CSS. As duas
 * cópias eram mantidas em sincronia por uma cerca.
 *
 * Uma rampa de cinco paradas **não cabe nesse arranjo**, e o motivo é mecânico:
 * o que o desenho precisa não é de cinco cores, é da cor **entre** duas delas —
 * e interpolar é conta, não valor. CSS não interpola em Lab, e a única forma de
 * ter a mesma cor nos dois lados seria escrever a conta duas vezes. Então a
 * rampa passou a ter **um** dono, este arquivo, e os dois consumidores leem
 * daqui: o `<canvas>` do mapa e a régua de rolagem (que recebe a tinta por
 * propriedade personalizada em `style`).
 *
 * O `@theme` deixou de carregar o token e passou a carregar **o porquê** — que
 * é o que ele sempre teve de mais valioso ali.
 *
 * ## Por que Lab, e não sRGB
 *
 * Entre `#ffe08a` e `#e05b22` o caminho em sRGB passa por um marrom sujo: os
 * canais andam em linha reta num espaço que não é perceptual, e a claridade
 * afunda no meio do trajeto. Em Lab a claridade é uma coordenada, e interpolar
 * L* linearmente é o que garante a única propriedade que esta rampa promete —
 * **a ordem**. É por isso que a interpolação é feita com `paraLab`/`corDeLab` de
 * `packages/core/src/design/cor.ts`, o mesmo instrumento com que a rampa foi
 * medida: uma segunda conta de cor neste repositório daria duas respostas para a
 * mesma pergunta.
 */
export const RAMPA_DE_DENSIDADE = [
  '#ffe08a',
  '#f5a13c',
  '#e05b22',
  '#b02a1a',
  '#760000',
] as const;

/**
 * As paradas já em Lab, uma vez, no carregamento do módulo.
 *
 * O mapa chama a interpolação uma vez por célula, e uma página movimentada tem
 * milhares delas: converter as cinco paradas a cada chamada seria refazer a
 * mesma conta cinco mil vezes por pintura.
 */
const PARADAS_EM_LAB: Lab[] = RAMPA_DE_DENSIDADE.map((hex) => {
  const cor = parseCor(hex);
  /* Um hexadecimal ilegível aqui é erro de digitação nesta lista, e ele tem de
     aparecer no ato: devolver preto silenciosamente daria uma rampa que ordena
     ao contrário sem ninguém notar. */
  if (!cor) throw new Error(`parada inválida na rampa de densidade: ${hex}`);
  return paraLab(cor);
});

/**
 * Os seis pontos canônicos em que a rampa é AMOSTRADA.
 *
 * Eles eram alfas; agora são **posições** na rampa, e os números continuam os
 * mesmos porque continuam vindo do mesmo lugar: são o piso e o teto de
 * `intensidade()` (`vview/mapa-geometria.ts`) com quatro amostras iguais entre
 * eles. 0,18 é onde a célula mais fraca do mapa cai, 1,00 é o centro da mais
 * quente.
 *
 * ## O mapa NÃO se quantiza nestes seis, e isso é de propósito
 *
 * O mapa pinta borrão radial e a posição na rampa é contínua; travar o centro em
 * seis valores moveria tinta sem mover informação nenhuma. Os degraus existem
 * para as duas outras coisas:
 *
 * 1. **Quem desenhar densidade em célula discreta** — calendário, coluna de
 *    tabela, barra de fita — usa esta escala em vez de inventar a dele. Foi a
 *    ausência dela que fez o mapa nascer com `rgba(63, 85, 107, α)` escrito à
 *    mão.
 * 2. **A cerca tem o que medir.** Monotonia é uma afirmação sobre uma sequência
 *    finita; sem esta lista não há sequência, e uma rampa contínua «prova»
 *    monotonia por construção sem provar que ela é *legível* — que é a
 *    afirmação que interessa, e que se mede no tamanho do passo.
 */
export const DEGRAUS_DE_DENSIDADE = [0.18, 0.34, 0.5, 0.66, 0.83, 1] as const;

/**
 * A cor da rampa numa posição, interpolada em Lab.
 *
 * `posicao` é o que `intensidade()` devolve — 0 é a ponta fraca, 1 a mais
 * quente. Fora do intervalo ela grampeia, porque uma posição fora da rampa não
 * é uma cor: é um defeito de quem chamou, e extrapolar o esconderia.
 */
export function corDaDensidade(posicao: number): Rgb {
  const x = Math.max(0, Math.min(1, posicao)) * (PARADAS_EM_LAB.length - 1);
  const i = Math.min(PARADAS_EM_LAB.length - 2, Math.floor(x));
  const f = x - i;
  const a = PARADAS_EM_LAB[i]!;
  const b = PARADAS_EM_LAB[i + 1]!;
  return corDeLab({
    L: a.L + (b.L - a.L) * f,
    a: a.a + (b.a - a.a) * f,
    b: a.b + (b.b - a.b) * f,
  });
}

/**
 * A tinta de uma posição, pronta para `fillStyle` e para `style`.
 *
 * O `alfa` existe para uma coisa só e não é para graduar densidade: é o
 * desvanecimento da BORDA do borrão radial, que é o que faz duas manchas
 * vizinhas somarem em vez de se recortarem. **A densidade agora é dita pela
 * cor**, e o centro de toda mancha é opaco — foi assim que a célula mais fraca
 * deixou de ser `#ebddd5` (croma 6,5 sobre branco, invisível) e passou a ser um
 * laranja de croma 60.
 */
export function tintaDaDensidade(posicao: number, alfa = 1): string {
  const c = corDaDensidade(posicao);
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alfa})`;
}

/**
 * ── UMA COR POR PERFIL — ORDEM DIRETA DO DONO, 05/09/2026 ───────────────────
 *
 * > *«uma alteração importante: quero cores dos gráficos diferentes para cada
 * > perfil. vc colocou tudo a mesma cor e ficou horrível, derrube qualquer
 * > limitação do auditor ou juiz sobre cores no gráfico, quero cores como
 * > amarelo, verde, azul, não importa, essa é uma ordem minha. associe uma cor
 * > para cada um dos vigiados nos gráficos que serão usados, a cor é automática
 * > e vc associa sozinho sem eu escolher.»*
 *
 * **O que caiu, e por ordem de quem.** A `Comparacao.tsx` desenhava as cinco
 * linhas com `CHART.accent` e escrevia a regra ao lado: *«uma tinta só, e a
 * distinção é de FORMA»*. Essa frase **cai aqui, para esta tela, por ordem do
 * dono** — não por decisão de forma, e não por medição que a tenha desmentido.
 * O que a substitui não é «pode tudo»: é a mesma disciplina que
 * `TINTA_DA_PLATAFORMA` já paga — contraste medido, ΔE ≥ 16 entre as tintas e
 * ΔE ≥ 16 de tudo que já significa alguma coisa nesta casa.
 *
 * ⚠ **O TRAÇO NÃO SAI.** Cor **acrescenta**, nunca substitui: estes gráficos
 * viajam em print no chat da equipe, e um print em cinzento perde a cor inteira.
 * O `stroke-dasharray` continua a ser o que identifica a linha em preto e branco
 * e para quem não distingue matiz, e a legenda promete por escrito ser «o mesmo
 * traço». Quem trocar cor por traço reabre o defeito com tinta nova por cima.
 *
 * ## AS TINTAS RECUSADAS, E A UMA QUE O DONO MANDOU ENTRAR ASSIM MESMO
 *
 * Ele pediu «amarelo, verde, azul». Verde e azul entraram sem discussão; o
 * quente é que teve de ser medido tinta a tinta, e quase todo ele caiu:
 *
 * | tinta tentada | o que a derrubou |
 * |---|---|
 * | mostarda `#8a6a12` | **4,30 no papel** — abaixo do piso de 4,5 |
 * | âmbar `#8a6410` | **4,23 na mesa** |
 * | bronze `#7a5a10` | **ΔE 14,6 do `warn`** — um perfil pintado da cor da pendência |
 * | verde-claro `#3a7a2a` | **4,46 no papel** |
 * | tijolo `#9c3b28` | **ΔE 13,5 da rampa** e 20,0 do `error` |
 * | magenta `#8e3277` | **nada** — passava os três testes, e foi a escolha até 05/09 |
 *
 * **A causa das quatro primeiras é uma só, e ela é o papel:** este papel é claro,
 * e um amarelo que chegue a 4,5:1 sobre `#ebedea` já escureceu tanto que ele *é*
 * o ocre — que nesta casa significa **pendência**. Pintar um concorrente de ocre
 * afirma uma avaria que não existe.
 *
 * ## ⚠ A DISPENSA DO LARANJA — DECISÃO DO DONO, 05/09/2026
 *
 * > *«derrube qualquer limitação do auditor ou juiz sobre cores no gráfico, quero
 * > cores como amarelo, verde, azul, não importa, essa é uma ordem minha»*
 *
 * O laranja `#a34a10` foi **recusado por esta obra** e **reposto por ordem do
 * dono**, com o número da recusa na mão. Ele passa o contraste (5,03 no papel) e
 * passa longe de `ok`, `warn` e `error` (≥ 20 dos três); a única parede era a
 * distância à `RAMPA_DE_DENSIDADE`, e ele decidiu pagar esse preço.
 *
 * **O preço, numa linha:** o laranja fica a **ΔE 17,7** da tinta que o mapa de
 * calor usa para dizer «muita coisa aqui», e **alguém pode ler quantidade onde é
 * só um perfil**.
 *
 * ⚠ **A regra NÃO foi apagada, e isso é o essencial.** A dispensa é **nomeada e
 * contável** — está em `DISPENSAS_DA_RAMPA`, logo abaixo, com a cor, o ΔE medido,
 * a data e a frase de quem a autorizou. A cerca continua a reprovar **qualquer**
 * tinta próxima da rampa que não esteja naquela lista, e reprova também se a
 * lista crescer. Apagar a asserção teria feito a segunda cor quente entrar amanhã
 * sem ninguém ver: **uma dispensa que não se conta deixa de ser dispensa e vira
 * costume.**
 *
 * ⚠ **E o preço tem um segundo endereço, que NÃO é desta obra.**
 * `a-rampa-de-densidade-mede.test.ts` varre as tintas com significado deste
 * arquivo contra 201 amostras da rampa, com piso 20 — o laranja passa a ser um
 * par abaixo do piso também lá. Aquela cerca **já está vermelha** por um par
 * anterior e alheio (`#f58529` da marca do Instagram, a ΔE 5,2), e ela reporta o
 * mínimo; então o laranja fica escondido atrás dele até o dia em que aquele for
 * consertado. **Não toquei naquela cerca** — ela não é desta área, e afrouxar
 * cerca alheia de passagem é pior que a colisão. Fica escrito aqui para quem a
 * consertar encontrar a dispensa em vez de um mistério.
 *
 * ## A MEDIÇÃO, com `packages/core/src/design/cor.ts`
 *
 * Contraste como linha e como texto sobre as superfícies do produto. **O papel
 * `#ebedea` é a que decide** — o quadro e as duas legendas vivem dentro de um
 * `Card`; a prancheta está na tabela por completude e não é superfície desta
 * tela (ela é o tampo do canvas de automação).
 *
 * | tinta | hex | papel | mesa | claro | prancheta |
 * |---|---|---:|---:|---:|---:|
 * | aço | `#3f556b` | 6,55 | 6,07 | 7,10 | 5,16 |
 * | jade | `#146b4a` | 5,52 | 5,11 | 5,98 | 4,35 |
 * | vinho | `#7a3b52` | 6,93 | 6,42 | 7,50 | 5,46 |
 * | turquesa | `#1b5b62` | 6,56 | 6,08 | 7,11 | 5,17 |
 * | laranja | `#a34a10` | 5,03 | 4,66 | 5,45 | 3,97 |
 * | índigo | `#43458f` | 7,17 | 6,65 | 7,77 | 5,66 |
 * | ameixa | `#5b4268` | 7,36 | 6,82 | 7,97 | 5,80 |
 *
 * Nenhuma desce de **5,03 no papel**; o piso de texto é 4,5. (Na prancheta o
 * laranja mede 3,97, e ela **não é superfície desta tela** — o quadro e as duas
 * legendas vivem dentro de um `Card`, que é papel.)
 *
 * **ΔE entre elas: o pior par é aço × turquesa a 16,1** — exactamente o par que
 * `TINTA_DA_PLATAFORMA` já tem (`meta` × `google_ads`), e o piso que esta casa já
 * aceitou. Os outros vinte pares passam de 20.
 *
 * **ΔE contra o que já significa alguma coisa**, nos três registros:
 *
 * | contra | pior par | ΔE | piso |
 * |---|---|---:|---:|
 * | estado (`ok`/`warn`/`error`) | jade × `ok` | **17,9** | 16 |
 * | entre si | aço × turquesa | **16,1** | 16 |
 * | quantidade (`RAMPA_DE_DENSIDADE`, 201 amostras) | **laranja** | **17,7** | 20 — **dispensado** |
 * | quantidade, sem contar o laranja | vinho | **40,5** | 20 |
 *
 * **A troca de 05/09 não mexeu nas duas primeiras linhas**, e isso foi conferido
 * antes de ela ser feita: o pior par interno e o pior par de estado não são do
 * laranja nem do magenta, e valem o mesmo com qualquer um dos dois no sétimo
 * lugar. O laranja só move a terceira linha, que é a dispensada.
 *
 * **Quatro das sete JÁ EXISTIAM nesta casa, e o reúso é deliberado.** Aço é o
 * `accent`; vinho, turquesa e ameixa são as tintas de série de `youtube`,
 * `google_ads` e `openai_ads` em `TINTA_DA_PLATAFORMA`. Não há colisão de
 * leitura porque **não há tela em que as duas famílias sejam desenhadas juntas**:
 * a comparação do V-Social não desenha série de plataforma de anúncio, e a marca
 * de rede que aparece ao lado usa `MARCA_DA_REDE`, que são outros hexadecimais.
 * Inventar quatro tons novos para não repetir uma tinta que ninguém vê ao lado
 * seria acrescentar quatro cores à casa sem consertar defeito nenhum.
 *
 * **Um tema só.** O painel do cliente não tem tema escuro — `index.css` não
 * declara `prefers-color-scheme` nem classe de tema —, então «legível nos dois
 * temas» é, aqui, legível sobre as quatro superfícies claras acima.
 *
 * ## SETE, E O NÚMERO NÃO É REDONDO POR ACASO
 *
 * O quadro da comparação deixa de ser legível acima de **cinco** linhas — é o
 * que diz `PADRAO_DO_TRACO` em `Comparacao.tsx`, e é por isso que a ficha existe
 * para tirar perfis de lá. Sete tintas para cinco linhas é a folga que garante
 * **cores distintas em qualquer conjunto desenhado**, sem precisar de uma oitava
 * que já não teria onde caber em ΔE.
 *
 * E sete contra cinco traços dá **35 pares cor+traço** antes de o desenho se
 * repetir; com cinco e cinco, o sexto perfil herdaria a cor **e** o traço do
 * primeiro ao mesmo tempo.
 */
export const TINTA_DO_PERFIL = [
  '#3f556b',
  '#146b4a',
  '#7a3b52',
  '#1b5b62',
  '#a34a10',
  '#43458f',
  '#5b4268',
] as const;

/** O nome de cada tinta, para a mensagem de uma cerca dizer qual delas falhou. */
export const NOME_DA_TINTA_DO_PERFIL: Record<string, string> = {
  '#3f556b': 'aço',
  '#146b4a': 'jade',
  '#7a3b52': 'vinho',
  '#1b5b62': 'turquesa',
  '#a34a10': 'laranja',
  '#43458f': 'índigo',
  '#5b4268': 'ameixa',
};

/**
 * ── AS DISPENSAS DA RAMPA — E ELAS SÃO CONTADAS, UMA A UMA ────────────────
 *
 * Uma tinta de perfil tem de ficar a **ΔE ≥ 20** de toda a `RAMPA_DE_DENSIDADE` —
 * a cor de QUANTIDADE do mapa de calor. Quem entra abaixo disso **não entra
 * calado**: entra por esta lista, com nome, número, data e a frase de quem
 * autorizou.
 *
 * ## Por que uma LISTA, e não uma asserção apagada
 *
 * Porque apagar a asserção resolve **este** caso e abre **todos** os seguintes: a
 * segunda cor quente entraria amanhã sem ninguém ver, e ninguém saberia dizer se
 * havia sido decidida ou se tinha escorregado. É assim que as exceções se
 * multiplicam nesta casa, e o sintoma é sempre o mesmo: a cerca fica verde.
 *
 * A cerca (`a-cor-do-perfil-sai-do-perfil-e-nao-da-fila.test.ts`) mede quatro
 * coisas sobre esta lista, e as quatro reprovam sozinhas:
 *
 * 1. **Toda tinta que colide está aqui.** Uma segunda cor quente fora da lista
 *    fica vermelha, com o nome dela na mensagem.
 * 2. **A lista não cresce.** O teto é `TETO_DE_DISPENSAS_DA_RAMPA`, e subí-lo é
 *    uma decisão com dono, não um efeito colateral.
 * 3. **Toda dispensa ainda é necessária.** No dia em que a tinta deixar de
 *    colidir, a cerca fica vermelha a pedir que a entrada seja APAGADA — dispensa
 *    que sobrevive ao conserto vira exceção permanente, e permissão prévia para
 *    uma cor que não precisava dela é pior ainda.
 * 4. **O ΔE declarado bate com o medido.** Um número escrito à mão que ninguém
 *    reconfere é a forma mais barata de uma dispensa mentir sobre o próprio preço.
 */
export interface DispensaDaRampa {
  /** A tinta dispensada — tem de ser uma de `TINTA_DO_PERFIL`. */
  cor: string;
  /** O ΔE medido contra a amostra mais próxima da rampa, no dia da decisão. */
  deltaE: number;
  /** `AAAA-MM-DD` da decisão. */
  em: string;
  /** Quem decidiu. Uma dispensa sem dono é uma dispensa que ninguém revoga. */
  quem: string;
  /** A frase que a autorizou, como ela foi dita. */
  frase: string;
  /** O que ela custa, em uma linha, na língua de quem lê a tela. */
  preco: string;
}

/** O teto da lista. Subí-lo é decisão do dono, e a cerca cobra o número. */
export const TETO_DE_DISPENSAS_DA_RAMPA = 1;

export const DISPENSAS_DA_RAMPA: readonly DispensaDaRampa[] = [
  {
    cor: '#a34a10',
    deltaE: 17.7,
    em: '2026-09-05',
    quem: 'o dono',
    frase:
      'derrube qualquer limitação do auditor ou juiz sobre cores no gráfico, quero cores ' +
      'como amarelo, verde, azul, não importa, essa é uma ordem minha',
    preco:
      'o laranja fica perto da tinta que o mapa de calor usa para dizer «muita coisa aqui», e ' +
      'alguém pode ler quantidade onde é só um perfil',
  },
];

/**
 * FNV-1a de 32 bits — a impressão digital do identificador do perfil.
 *
 * Não é criptografia e não precisa ser: o que se pede dela é **espalhar** e ser
 * a mesma conta amanhã. `Math.imul` porque a multiplicação de 32 bits em ponto
 * flutuante perde os bits altos, e sem eles dois ids com o mesmo prefixo caem na
 * mesma tinta — que é o caso normal de identificador desta casa.
 */
function impressaoDigital(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * A tinta de cada perfil — **do perfil, e nunca da posição dele na lista**.
 *
 * ## O defeito que esta função existe para não cometer
 *
 * `paleta[i % 7]` sobre o índice do laço é a versão de uma linha disto, e ela
 * mente uma vez por gesto: entra um vigiado novo no meio da lista e **todos os
 * de baixo trocam de cor**. Quem aprendeu «a linha verde é o @quantbrasil» reabre
 * a tela no dia seguinte e a verde é outro canal — sem nada acusar, porque a
 * legenda troca junto e continua «coerente».
 *
 * A tinta sai da **impressão digital do id**, que é a única coisa do perfil que
 * não muda. Tirar um perfil do quadro não mexe em ninguém, reordenar a lista não
 * mexe em ninguém, e o mesmo perfil abre com a mesma cor em qualquer sessão.
 *
 * ## O DESDOBRAMENTO, E O PREÇO DELE ESTÁ DECLARADO
 *
 * Impressão digital pura **não basta**, e o número é duro: cinco perfis em sete
 * tintas colidem em **85% dos sorteios** (1 − 7·6·5·4·3 ÷ 7⁵). Uma tela em que
 * dois dos cinco saem da mesma cor é meia regressão para o que o dono chamou de
 * horrível, e a ordem também diz *«as cores têm de ser distinguíveis entre si»*.
 *
 * Então quem chega numa tinta já tomada **anda para a frente** até a primeira
 * livre. A ordem em que se resolve é a dos **ids**, e não a da lista: é isso que
 * mantém o resultado igual quando a lista é reordenada ou filtrada.
 *
 * ⚠ **O que isto NÃO garante, dito em voz alta.** Um perfil cadastrado hoje,
 * cujo id ordene antes de um existente **e** cuja tinta preferida seja a mesma,
 * fica com ela e empurra o existente um degrau. É o único caso em que uma cor se
 * move, e ele é o preço dos 85% acima: as duas cláusulas da ordem — «não troca
 * quando um terceiro entra» e «distinguíveis entre si» — não são simultaneamente
 * satisfazíveis com sete tintas, e esta é a troca **escolhida**, não a que passou
 * despercebida.
 *
 * ## Acima de sete perfis
 *
 * A paleta acaba e a tinta preferida é devolvida como está — duas linhas repetem
 * cor, e quem as separa é o traço e o retrato. É o mesmo desfecho de
 * `PADRAO_DO_TRACO` acima de cinco, pela mesma razão: o conserto de um quadro com
 * oito linhas não é uma oitava tinta, é tirar linhas do quadro.
 */
export function tintasDosPerfis(ids: readonly string[]): Map<string, string> {
  const unicos = [...new Set(ids)].sort();
  const tomadas = new Set<number>();
  const tintas = new Map<string, string>();
  for (const id of unicos) {
    const preferida = impressaoDigital(id) % TINTA_DO_PERFIL.length;
    let escolhida = preferida;
    for (let passo = 0; passo < TINTA_DO_PERFIL.length; passo += 1) {
      const candidata = (preferida + passo) % TINTA_DO_PERFIL.length;
      if (!tomadas.has(candidata)) {
        escolhida = candidata;
        break;
      }
    }
    tomadas.add(escolhida);
    tintas.set(id, TINTA_DO_PERFIL[escolhida]!);
  }
  return tintas;
}
